import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  TraceSourceFilter,
  TraceViewSettings,
} from '@terra/ui-design-system';

type TraceViewSettingsState = TraceViewSettings & {
  setShowHttpViewGlobal: (show: boolean) => void;
  setCardHttpView: (cardId: string, showHttp: boolean) => void;
  shouldShowHttpView: (cardId: string) => boolean;
  setShowDetailedView: (show: boolean) => void;
  setIsPlanExpanded: (expanded: boolean) => void;
  setShowCondensedView: (show: boolean) => void;
  setSourceFilter: (filter: TraceSourceFilter) => void;
  setShowHiddenSteps: (show: boolean) => void;
  resetSettings: () => void;
};

const STORAGE_KEY = 'terra-proxy-trace-view-settings';

const initialState: TraceViewSettings = {
  showHttpViewGlobal: false,
  httpViewPerCard: {},
  showDetailedView: true,
  isPlanExpanded: true,
  showCondensedView: false,
  sourceFilter: 'all',
  showHiddenSteps: false,
};

export const useTraceViewSettingsStore = create<TraceViewSettingsState>()(
  persist(
    (set, get) => ({
      ...initialState,

      setShowHttpViewGlobal: (show: boolean) =>
        set({
          showHttpViewGlobal: show,
          httpViewPerCard: {},
        }),

      setCardHttpView: (cardId: string, showHttp: boolean) =>
        set((state) => ({
          httpViewPerCard: {
            ...state.httpViewPerCard,
            [cardId]: showHttp,
          },
        })),

      shouldShowHttpView: (cardId: string) => {
        const state = get();
        const perCardOverride = state.httpViewPerCard[cardId];
        if (perCardOverride !== undefined) {
          return perCardOverride;
        }
        return state.showHttpViewGlobal;
      },

      setShowDetailedView: (show: boolean) => set({ showDetailedView: show }),

      setIsPlanExpanded: (expanded: boolean) =>
        set({ isPlanExpanded: expanded }),

      setShowCondensedView: (show: boolean) => set({ showCondensedView: show }),

      setSourceFilter: (filter: TraceSourceFilter) =>
        set({ sourceFilter: filter }),

      setShowHiddenSteps: (show: boolean) => set({ showHiddenSteps: show }),

      resetSettings: () => set(initialState),
    }),
    {
      name: STORAGE_KEY,
      partialize: (state) => ({
        showHttpViewGlobal: state.showHttpViewGlobal,
        httpViewPerCard: state.httpViewPerCard,
        showDetailedView: state.showDetailedView,
        isPlanExpanded: state.isPlanExpanded,
        showCondensedView: state.showCondensedView,
        sourceFilter: state.sourceFilter,
        showHiddenSteps: state.showHiddenSteps,
      }),
    },
  ),
);
